// Identity hub client (docs/IDENTITY_HUB_PHASE1_BUILD.md, T3).
//
// With REACT_APP_AUTH_HUB=on the access token lives here, in memory, and is
// renewed from auth.xirugias.com with the HttpOnly refresh cookie the hub
// set at login. With the flag off every function falls back to the legacy
// `token` cookie, so the rest of the client can call getAccessToken()
// unconditionally.
//
// This module imports nothing from the app (only js-cookie) so authHelpers,
// the reducer and the socket module can all depend on it without cycles.
import cookie from 'js-cookie';

// The build flag decides for everyone; one browser can opt in or out with
// localStorage.xirugiasAuthHub = 'on' | 'off' (set from the console), which
// is how the hub is piloted on www before the build flag is turned on, and
// how one person can fall back to the old login without a rebuild.
export const HUB_OVERRIDE_KEY = 'xirugiasAuthHub';
const hubOverride = () => {
    try { return typeof localStorage !== 'undefined' ? localStorage.getItem(HUB_OVERRIDE_KEY) : null; } catch (e) { return null; }
};
export const hubEnabled = () => {
    const override = hubOverride();
    if (override === 'on' || override === 'off') return override === 'on';
    return process.env.REACT_APP_AUTH_HUB === 'on';
};
export const hubUrl = (process.env.REACT_APP_AUTH_HUB_URL || 'https://auth.xirugias.com').replace(/\/+$/, '');

// Renew this long before the token expires (a request sent with 1 s left
// would reach the server expired).
const SKEW_MS = 30 * 1000;
// And schedule the background renewal this long before expiry.
const RENEW_BEFORE_MS = 60 * 1000;

// Retry a renewal that failed for a reason other than 401 (hub down,
// offline) this often while the current token is still good.
const RETRY_MS = 30 * 1000;

let accessToken = null;
let expiresAt = 0;
let inflight = null;
let timer = null;
// Bumped by clearAccessToken: a refresh that was in flight when the person
// logged out must not put its token back afterwards.
let generation = 0;

const legacyToken = () => {
    try { return cookie.get('token') || null; } catch (e) { return null; }
};

const fresh = () => hubEnabled() && accessToken && expiresAt - Date.now() > 0;

// Synchronous: what every request puts in its Authorization header.
export const getAccessToken = () => (fresh() ? accessToken : legacyToken());

export const setAccessToken = (token, expiresInSeconds) => {
    accessToken = token || null;
    expiresAt = token ? Date.now() + (Number(expiresInSeconds) || 900) * 1000 : 0;
    if (timer) clearTimeout(timer);
    timer = null;
    if (token && typeof window !== 'undefined') {
        const inMs = Math.max(expiresAt - Date.now() - RENEW_BEFORE_MS, 5 * 1000);
        timer = setTimeout(() => { refreshAccessToken(); }, inMs);
    }
};

export const clearAccessToken = () => { generation += 1; setAccessToken(null); };

// Hub hiccup or offline: keep the token we have and try again soon.
const keepAndRetry = () => {
    if (!fresh()) return null;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { refreshAccessToken(); }, Math.min(RETRY_MS, Math.max(expiresAt - Date.now() - SKEW_MS, 1000)));
    return accessToken;
};

// One refresh at a time: every caller that hits a 401 during the same
// instant shares the same request and gets the same new token.
export const refreshAccessToken = () => {
    if (!hubEnabled()) return Promise.resolve(legacyToken());
    // A legacy (cookie) session in this tab: the hub cookie, if any, is
    // someone else's session and never this tab's.
    if (legacyToken() && !accessToken) return Promise.resolve(null);
    if (inflight) return inflight;
    const started = generation;
    // Whose session this tab is running, to notice when the hub cookie now
    // names someone else (signed in from another tab).
    const before = accessToken ? tokenUserId(accessToken) : null;
    inflight = fetch(`${hubUrl}/auth/refresh?aud=xirugias`, { method: 'POST', credentials: 'include' })
        .then(async (res) => {
            if (res.status === 401) {
                if (started === generation) clearAccessToken();
                return null;
            }
            if (!res.ok) return keepAndRetry();
            const body = await res.json();
            if (started !== generation) return null; // logged out meanwhile
            // Never go on as the person on screen with someone else's token.
            if (before && tokenUserId(body.accessToken) !== before) return reloadAsHubUser();
            setAccessToken(body.accessToken, body.expiresIn);
            return body.accessToken;
        })
        .catch(() => keepAndRetry())
        .finally(() => { inflight = null; });
    return inflight;
};

// The `_id` a token names (the company user), without verifying it: the
// server verifies; here it only tells whether the stored profile is this
// person's.
export const tokenUserId = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload && payload._id ? String(payload._id) : null;
    } catch (e) {
        return null;
    }
};

// The token to use right now, renewed first when it is about to expire.
export const ensureAccessToken = async () => {
    if (!hubEnabled()) return legacyToken();
    if (accessToken && expiresAt - Date.now() > SKEW_MS) return accessToken;
    return (await refreshAccessToken()) || legacyToken();
};

// Where the hub should send the browser back: the page the person was on,
// or /app when they were already on a login screen.
export const hubReturnTo = () => {
    const { origin, pathname, href } = window.location;
    return pathname.startsWith('/auth') ? `${origin}/app` : href;
};

export const hubLoginUrl = (reason) => {
    const url = `${hubUrl}/login?return_to=${encodeURIComponent(hubReturnTo())}`;
    return reason ? `${url}&reason=${encodeURIComponent(reason)}` : url;
};

export const redirectToHubLogin = (reason) => {
    window.location.assign(hubLoginUrl(reason));
};

// Ends the hub session (revokes the refresh token). Local cleanup is the
// caller's (authHelpers.signout does both). Always sent when the hub is on,
// even without a token in memory (it may have expired, or the page may have
// just loaded): the cookie is what identifies the session. keepalive lets
// the request finish while the browser navigates to the login page.
export const hubLogout = () => {
    const legacySession = !!legacyToken() && !accessToken;
    clearAccessToken();
    // Signing out of a legacy session leaves any hub session in this
    // browser alone: it belongs to someone else.
    if (!hubEnabled() || legacySession) return Promise.resolve();
    return fetch(`${hubUrl}/auth/logout`, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => {});
};

const clearStoredProfile = () => {
    try {
        localStorage.removeItem('user');
        localStorage.removeItem('configuration');
        cookie.remove('subToken');
    } catch (e) { /* storage unavailable */ }
};

// Turns the hub login on or off for this device ('on' | 'off') and ends the
// session that belongs to the other login, so the next screen is the right
// login page. Returns false for any other value.
export const setHubSwitch = (value) => {
    if (value !== 'on' && value !== 'off') return false;
    if (value === 'off') {
        // Only a device that was on the hub has a hub session to end; revoke
        // it while the hub is still on. A session of the old login stays:
        // its profile must not be wiped from under its cookie.
        if (hubEnabled()) {
            hubLogout();
            if (!legacyToken()) clearStoredProfile();
        }
    } else {
        clearAccessToken();
        if (legacyToken()) {
            // The old login's session would keep this device off the hub.
            try { cookie.remove('token'); } catch (e) { /* storage unavailable */ }
            clearStoredProfile();
        }
    }
    try { localStorage.setItem(HUB_OVERRIDE_KEY, value); } catch (e) { /* storage unavailable */ }
    return true;
};

// A link with ?hub=on or ?hub=off sets the switch on this device: the way to
// pilot the hub from a home-screen app, which has no console. It also ends
// the session that belongs to the other login, so the next screen is the
// right login page, and drops the parameter from the address. Runs before
// the first render. Returns the value applied, or null.
export const applyHubSwitchFromUrl = () => {
    if (typeof window === 'undefined') return null;
    let url;
    try { url = new URL(window.location.href); } catch (e) { return null; }
    const value = url.searchParams.get('hub');
    if (!setHubSwitch(value)) return null;
    url.searchParams.delete('hub');
    try { window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash); } catch (e) { /* ignore */ }
    return value;
};

// GET /api/users/bootstrap with a hub token: the user and configuration
// the app keeps in localStorage, plus the subscription summary, which the
// caller hands to the store (subscription-context fetches it again on
// every load). Throws when it fails; `refused` is set when the server said
// the session or the account is no longer valid (401, 403, 404 user not
// found), as opposed to an outage (5xx, network, a proxy's 4xx, or no
// answer within `timeoutMs`, given only by callers that can fall back on
// the stored profile). `store: false` leaves localStorage alone, for a
// caller that first checks the answer is still wanted.
const REFUSALS = [401, 403, 404];
export const BOOTSTRAP_TIMEOUT_MS = 5000;
const fetchBootstrap = async (apiUrl, token, { store = true, timeoutMs = 0 } = {}) => {
    const controller = timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        const res = await fetch(`${apiUrl}users/bootstrap`, {
            headers: { Authorization: `bearer ${token}` },
            ...(controller ? { signal: controller.signal } : {})
        });
        if (!res.ok) {
            const err = new Error(`bootstrap ${res.status}`);
            err.refused = REFUSALS.includes(res.status);
            throw err;
        }
        // The limit covers the body too: headers then a stalled body is no answer.
        const body = await res.json();
        if (store) storeProfile(body);
        return body;
    } finally {
        if (timer) clearTimeout(timer);
    }
};

const storeProfile = (body) => {
    localStorage.setItem('user', JSON.stringify(body.user));
    localStorage.setItem('configuration', JSON.stringify(body.configuration));
};

// Sign in from the app's own login form: the hub sets its cookie from this
// page, so a home-screen PWA never leaves its own window (an out-of-scope
// hop would land the cookie in another cookie jar on iPhone). Resolves to
// {ok: true, accessToken, user, subscription, configuration} or
// {ok: false, error, message}; `aud_not_allowed` means a doctor account,
// which the caller sends through the legacy login instead.
export const hubLogin = (apiUrl, { email, password }) => hubSignIn(apiUrl, '/auth/login', { email, password, aud: 'xirugias' });

// Login by email link (H5): the hub emails a link that opens on
// /auth/email-link-login/<token> here, plus a 6-digit code to type in the
// app (a link tapped in an email opens Safari, not the home-screen app).
// hubLinkRequest resolves to {ok: true, message} whatever the email (the hub
// never says which emails have accounts) or {ok: false, error, message};
// `aud_not_allowed` means a doctor account, which the caller sends through
// the legacy link request instead. hubLinkLogin takes {token} or
// {email, code} and resolves like hubLogin.
export const hubLinkRequest = async ({ email }) => {
    const res = await hubPost('/auth/link/request', { email, aud: 'xirugias', path: '/auth/email-link-login' });
    if (!res.ok) return { ok: false, error: res.error, message: res.message || 'No se pudo enviar el correo. Intenta de nuevo.' };
    return { ok: true, message: res.body.message };
};
export const hubLinkLogin = (apiUrl, { token, email, code }) =>
    hubSignIn(apiUrl, '/auth/link/login', token ? { token, aud: 'xirugias' } : { email, code, aud: 'xirugias' });
// Errors the hub answers on purpose on the link endpoints. Anything else
// (a hub without the feature on, 401 no_session, a 404, a network error)
// means the hub cannot do links right now, and the old Xirugias link is the
// way in, so "Recuperar contraseña" never dies with the hub.
const HUB_LINK_ERRORS = ['too_many_attempts', 'bad_request', 'mail_failed', 'aud_not_allowed'];
export const hubLinkUnavailable = (error) => !HUB_LINK_ERRORS.includes(error);
// Right after a link or code login the hub lets the person set a password
// (the email was just proved): the hub's "olvidé mi contraseña".
export const hubSetPassword = async (newPassword) => {
    const res = await hubPost('/auth/password', { newPassword });
    if (!res.ok) return { ok: false, error: res.error, message: res.message || 'No se pudo guardar la contraseña.' };
    // The hub bumped tokenVersion; the token in memory carries the old one
    // and the API would refuse it. The session already has the new value.
    const token = await refreshAccessToken();
    return { ok: true, accessToken: token };
};
// The device's hub switch as stored ('on' | 'off' | null), to put it back
// after a failed attempt.
export const getHubSwitch = () => hubOverride();
export const restoreHubSwitch = (value) => {
    try {
        if (value === 'on' || value === 'off') localStorage.setItem(HUB_OVERRIDE_KEY, value);
        else localStorage.removeItem(HUB_OVERRIDE_KEY);
    } catch (e) { /* storage unavailable */ }
};
// A hub link token is base64url (no dots); the legacy email link carries a JWT.
export const isHubLinkToken = (token) => typeof token === 'string' && token.length >= 32 && !token.includes('.');

const hubPost = async (path, payload) => {
    let res;
    try {
        res = await fetch(`${hubUrl}${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
    } catch (e) {
        return { ok: false, error: 'network', message: 'No se pudo contactar el servicio de acceso. Intenta de nuevo.' };
    }
    let body = {};
    try { body = await res.json(); } catch (e) { body = {}; }
    if (!res.ok) return { ok: false, error: body.error || `http_${res.status}`, message: body.message, body };
    return { ok: true, body };
};

const hubSignIn = async (apiUrl, path, payload) => {
    const res = await hubPost(path, payload);
    if (!res.ok) return { ok: false, error: res.error, message: res.message || 'No se pudo iniciar sesión. Intenta de nuevo.' };
    const body = res.body;
    clearStoredProfile();
    // A leftover legacy cookie would win over the hub session on the next
    // page load (bootstrapHubSession leaves a legacy session alone).
    try { cookie.remove('token'); } catch (e) { /* storage unavailable */ }
    setAccessToken(body.accessToken, body.expiresIn);
    try {
        const profile = await fetchBootstrap(apiUrl, body.accessToken);
        return { ok: true, accessToken: body.accessToken, user: profile.user, subscription: profile.subscription, configuration: profile.configuration };
    } catch (e) {
        clearAccessToken();
        clearStoredProfile();
        return { ok: false, error: 'bootstrap', message: 'No se pudo cargar la sesión. Intenta de nuevo.' };
    }
};

// Before the first render: turn the hub cookie into an access token and, on
// a fresh browser, into the user/configuration the app keeps in
// localStorage (GET /api/users/bootstrap, T1). Returns true when a hub
// session is in place. A legacy cookie session is left alone.
export const bootstrapHubSession = async (apiUrl) => {
    if (!hubEnabled() || typeof window === 'undefined') return false;
    if (legacyToken()) return false;
    const token = await refreshAccessToken();
    if (!token) {
        // No hub session: a profile left in localStorage belongs to nobody now.
        clearStoredProfile();
        return false;
    }
    let user = null;
    try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch (e) { user = null; }
    // This token's user already has a profile stored (and not someone else
    // on a shared browser).
    const sameUser = Boolean(user && user._id && String(user._id) === tokenUserId(token));
    try {
        // Always asked again, even when a profile is stored: the hub session
        // lasts weeks, and a role, permission or company configuration change
        // must reach the screen on the next load, not at the next login.
        // With this person's profile stored, a slow server must not hold the
        // first render: past the limit the stored copy is used.
        await fetchBootstrap(apiUrl, token, sameUser ? { timeoutMs: BOOTSTRAP_TIMEOUT_MS } : {});
        return true;
    } catch (e) {
        // Server down or failing: this person's stored profile keeps the app
        // usable until the next load (or useProfileRefresh) gets through.
        if (sameUser && !e.refused) return true;
        // A refusal, or nothing usable stored: a token without a profile would
        // render the app half signed in; drop both so the person lands on the
        // login page instead.
        clearAccessToken();
        clearStoredProfile();
        return false;
    }
};

// While the app is open (useProfileRefresh): the same users/bootstrap, so a
// role, permission or configuration change shows without reloading. Resolves
// to the bootstrap body, or null when there is nothing to do or it failed
// (the stored profile stays as it was). `userId` is the person on screen:
// the answer is used only for that same person, and only if nobody signed
// out or in while it was on its way. A legacy (non-hub) session is left
// alone, as on page load: a hub cookie in the same browser may belong to
// someone else.
export const refreshStoredProfile = async (apiUrl, userId) => {
    if (!hubEnabled() || legacyToken() || !userId) return null;
    const started = generation;
    const token = await ensureAccessToken();
    if (!token || !fresh() || started !== generation) return null;
    if (tokenUserId(token) !== String(userId)) return reloadAsHubUser();
    let body;
    try {
        body = await fetchBootstrap(apiUrl, token, { store: false, timeoutMs: BOOTSTRAP_TIMEOUT_MS });
    } catch (e) {
        return null;
    }
    if (started !== generation || !accessToken) return null;
    // A renewal while the answer was on its way may have brought someone
    // else's session (signed in from another tab).
    if (tokenUserId(accessToken) !== String(userId)) return reloadAsHubUser();
    if (!body || !body.user || String(body.user._id) !== String(userId)) return null;
    // localStorage is shared by every tab: write only over this same
    // person's profile. Empty means another tab signed out (or someone else
    // is mid sign-in); someone else's profile means another tab signed them in.
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem('user') || 'null'); } catch (e) { stored = null; }
    if (!stored || String(stored._id) !== String(userId)) return null;
    storeProfile(body);
    return body;
};

// The hub session in this browser is now someone else's (signed in from
// another tab): this page must not go on as the old person with the new
// person's token. Reloading opens it as whoever the hub says.
// Never settles: the page is about to reload, so callers must not go on and
// treat this as a failed renewal (a 401 handler would sign out, and that
// would end the OTHER person's hub session and wipe their stored profile).
const reloadAsHubUser = () => {
    clearAccessToken();
    // Free the shared renewal: if the reload is cancelled ("Stay on page"
    // during an upload), later calls must ask the hub again, not wait on
    // this promise for ever.
    inflight = null;
    try { window.location.reload(); } catch (e) { /* not in a browser */ }
    return new Promise(() => {});
};

// Tests only.
export const _resetForTests = () => { clearAccessToken(); inflight = null; generation = 0; };
